// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { RxCollection, RxQuery, RxDocument } from 'rxdb';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { QueryOptions, QueryResult } from './types';

/**
 * Framework-agnostic query operator
 *
 * Wraps RxDB queries with reactive observables that work in any environment.
 * Provides both imperative (exec) and reactive (observable) APIs.
 *
 * @example
 * ```typescript
 * // Imperative usage (Node.js, CLI)
 * const query = new QueryOperator(collection, { selector: { status: 'active' } });
 * const results = await query.exec();
 *
 * // Reactive usage (UI frameworks)
 * query.observable.subscribe(result => {
 *   console.log('Data updated:', result.data);
 * });
 * ```
 */
export class QueryOperator<T> {
  private collection: RxCollection;
  private query: RxQuery<T>;
  private resultSubject: BehaviorSubject<QueryResult<T>>;
  private subscription?: Subscription;
  private _isDestroyed = false;

  constructor(collection: RxCollection, options: QueryOptions<T>) {
    this.collection = collection;
    this.query = this.buildQuery(options);
    this.resultSubject = new BehaviorSubject<QueryResult<T>>({
      data: [],
      isLoading: true,
      error: null,
    });

    this.initialize();
  }

  /**
   * Whether this operator has been destroyed.
   * Used by useQuery to detect StrictMode remounts with a stale operator.
   */
  get isDestroyed(): boolean {
    return this._isDestroyed;
  }

  /**
   * Build RxDB query from options
   */
  private buildQuery(options: QueryOptions<T>): RxQuery<T> {
    // Build MangoQuery object with all options at once
    const mangoQuery: any = {
      selector: options.selector,
    };

    if (options.sort) {
      mangoQuery.sort = options.sort;
    }

    if (options.limit !== undefined) {
      mangoQuery.limit = options.limit;
    }

    if (options.skip !== undefined) {
      mangoQuery.skip = options.skip;
    }

    // Pass complete MangoQuery object to find()
    return this.collection.find(mangoQuery) as RxQuery<T>;
  }

  /**
   * Initialize reactive subscription to query results.
   * Safe to call multiple times (e.g., after reinitialize).
   */
  private initialize(): void {
    // Clean up any existing subscription before re-subscribing
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.query.$
      .pipe(map((docs: RxDocument<T>[]) => docs.map((doc: RxDocument<T>) => doc.toJSON() as T)))
      .subscribe({
        next: (data) => {
          this.resultSubject.next({
            data,
            isLoading: false,
            error: null,
          });
        },
        error: (error) => {
          this.resultSubject.next({
            data: [],
            isLoading: false,
            error,
          });
        },
      });
  }

  /**
   * Re-initialize after destroy. Needed for React StrictMode compatibility
   * where useMemo caches the operator but useEffect cleanup destroys it,
   * then the remount needs to resubscribe.
   */
  reinitialize(): void {
    if (!this._isDestroyed) return;
    this._isDestroyed = false;
    // Create a fresh BehaviorSubject since the old one was completed
    this.resultSubject = new BehaviorSubject<QueryResult<T>>({
      data: [],
      isLoading: true,
      error: null,
    });
    this.initialize();
  }

  /**
   * Execute query imperatively and return results as Promise
   *
   * Use in Node.js scripts, CLI tools, and other imperative contexts.
   *
   * @returns Promise resolving to array of documents
   */
  async exec(): Promise<T[]> {
    const docs = await this.query.exec();
    return docs.map((doc: RxDocument<T>) => doc.toJSON() as T);
  }

  /**
   * Get reactive observable of query results
   *
   * Subscribe to this in UI frameworks for automatic updates.
   *
   * @returns Observable that emits QueryResult on every data change
   */
  get observable(): Observable<QueryResult<T>> {
    // Auto-reinitialize if destroyed (React StrictMode remount)
    if (this._isDestroyed) {
      this.reinitialize();
    }
    return this.resultSubject.asObservable();
  }

  /**
   * Get current query result synchronously
   *
   * @returns Current QueryResult value
   */
  get value(): QueryResult<T> {
    return this.resultSubject.value;
  }

  /**
   * Refetch query results imperatively
   *
   * @returns Promise resolving to current query results
   */
  async refetch(): Promise<T[]> {
    return this.exec();
  }

  /**
   * Cleanup subscriptions and complete observables
   *
   * Call this when the query is no longer needed to prevent memory leaks.
   */
  destroy(): void {
    this._isDestroyed = true;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.resultSubject.complete();
  }
}
